import { doc, firestore, onSnapshot } from '@ps/functions/firebase/firestore';
import type { CommunityChannelMember as CanonicalCommunityChannelMember } from '@ps/models/lib/community-channel';

import type { ChannelSubscriptionLevel } from '../../types/ChannelSubscriptionLevel';
import type { CommunityChannelMember } from '../../types/CommunityChannelMember';
import type { CommunityChannelMemberStatus } from '../../types/CommunityChannelMemberStatus';

/**
 * Subscribe to the current user's membership doc in a channel —
 * delivers live `muted` / `subscribedToMentions` flips so the
 * Notifications toggle in the channel-info popover stays accurate
 * across tabs.
 *
 * Emits `null` when the doc doesn't exist (signed-in user isn't a
 * member of the channel — for the creator's own channels this
 * is normal until they explicitly join, so the UI should treat
 * `null` as "not muted, not subscribed to mentions" defaults).
 */
export function subscribeToMyChannelMembership(
  creatorId: string,
  channelId: string,
  userId: string,
  onChange: (member: CommunityChannelMember | null) => void,
  onError?: (error: Error) => void,
): () => void {
  const db = firestore();
  if (!db) return () => {};

  const ref = doc(
    db,
    'stores',
    creatorId,
    'communityChannels',
    channelId,
    'members',
    userId,
  );
  const unsubscribe = onSnapshot(
    ref,
    snapshot => {
      const data = snapshot.data();
      if (!data) {
        onChange(null);
        return;
      }
      // Typed view of the canonical member doc for the access-gating fields
      // (`status` / `minimumRequiredSubscriptionLevel`), bridged to the slice's
      // nominal enums the same way `firestoreDocToChannel` bridges the channel
      // doc's visibility / subscription-level.
      const member = data as CanonicalCommunityChannelMember;
      // The backend member doc nests the flags under `notificationSettings`
      // (see `@ps/models` `CommunityChannelMemberNotificationSettings`); fall
      // back to any flat fields for older docs.
      const ns = data.notificationSettings ?? data;
      onChange({
        id: snapshot.id,
        channelId,
        userId,
        status: member.status as unknown as CommunityChannelMemberStatus,
        minimumRequiredSubscriptionLevel:
          member.minimumRequiredSubscriptionLevel as unknown as ChannelSubscriptionLevel,
        muted: ns.muted ?? false,
        subscribedToMentions: ns.subscribedToMentions ?? true,
        subscribedToUpdatesOfCreator: ns.subscribedToUpdatesOfCreator ?? true,
        subscribedToUpdatesOfMessages: ns.subscribedToUpdatesOfMessages ?? [],
      });
    },
    error => onError?.(error),
  );
  return unsubscribe;
}
